import React, { useEffect, useState } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { AppShell } from "@/components/layout/AppShell";
import LoginPage from "@/pages/LoginPage";
import OverviewPage from "@/pages/OverviewPage";
import InboxPage from "@/pages/InboxPage";
import PipelinePage from "@/pages/PipelinePage";
import OpportunityPage from "@/pages/OpportunityPage";
import QuotesPage from "@/pages/QuotesPage";
import TendersPage from "@/pages/TendersPage";
import TenderPage from "@/pages/TenderPage";
import CustomersPage from "@/pages/CustomersPage";
import CustomerPage from "@/pages/CustomerPage";
import ProductsPage from "@/pages/ProductsPage";
import InsightsPage from "@/pages/InsightsPage";
import SettingsPage from "@/pages/SettingsPage";
import PublicQuotePage from "@/pages/PublicQuotePage";
import PublicDiscoveryPage from "@/pages/PublicDiscoveryPage";
import WorkspaceHomePage from "@/pages/WorkspaceHomePage";
import WorkspacePage from "@/pages/WorkspacePage";
import MediabilityProposalPage from "@/pages/MediabilityProposalPage";
import { IntelLayout } from "@/components/intel/IntelProvider";
import IntelTodayPage from "@/pages/IntelTodayPage";
import IntelRadarPage from "@/pages/IntelRadarPage";
import IntelCompaniesPage from "@/pages/IntelCompaniesPage";
import IntelCompanyPage from "@/pages/IntelCompanyPage";
import IntelOpportunitiesPage from "@/pages/IntelOpportunitiesPage";
import { MediabilityMark } from "@/components/Logo";
import "@/App.css";

function RequireAuth({ children }) {
  const { user, loading } = useAuth();
  const loc = useLocation();
  if (loading) {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-5 bg-background" data-testid="app-loading">
        <MediabilityMark className="h-10 w-10 fade-up" />
        <div className="flex items-center gap-2.5 text-[13px] text-muted-foreground">
          <span className="h-1.5 w-1.5 rounded-full bg-primary pulse-dot" /> Loading Mediability Commercial OS
        </div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" state={{ from: loc }} replace />;
  return children;
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    const el = document.getElementById("main-scroll");
    if (el) el.scrollTo({ top: 0 });
  }, [pathname]);
  return null;
}

function App() {
  return (
    <AuthProvider>
      <TooltipProvider delayDuration={200}>
        <BrowserRouter>
          <ScrollToTop />
          <Routes>
            {/* Public proposal (Kviteberg -> Mediability) lives on the root; the product starts at /login */}
            <Route path="/" element={<MediabilityProposalPage />} />
            <Route path="/tilbud" element={<Navigate to="/" replace />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/q/:token" element={<PublicQuotePage />} />
            <Route path="/project/:token" element={<PublicDiscoveryPage />} />
            {/* Focused, full-viewport surface - lives outside the app shell */}
            <Route
              path="/workspace/:id"
              element={
                <RequireAuth>
                  <WorkspacePage />
                </RequireAuth>
              }
            />
            <Route
              element={
                <RequireAuth>
                  <AppShell />
                </RequireAuth>
              }
            >
              <Route path="/overview" element={<OverviewPage />} />
              <Route path="/inbox" element={<InboxPage />} />
              <Route path="/inbox/:threadId" element={<InboxPage />} />
              <Route path="/workspace" element={<WorkspaceHomePage />} />
              {/* Sales Intelligence - shared overlays (Copilot, signal deep-dive, create opportunity) live in IntelLayout */}
              <Route element={<IntelLayout />}>
                <Route path="/intel" element={<Navigate to="/intel/today" replace />} />
                <Route path="/intel/today" element={<IntelTodayPage />} />
                <Route path="/intel/radar" element={<IntelRadarPage />} />
                <Route path="/intel/companies" element={<IntelCompaniesPage />} />
                <Route path="/intel/companies/:id" element={<IntelCompanyPage />} />
                <Route path="/intel/opportunities" element={<IntelOpportunitiesPage />} />
              </Route>
              <Route path="/pipeline" element={<PipelinePage />} />
              <Route path="/opportunities/:id" element={<OpportunityPage />} />
              <Route path="/quotes" element={<QuotesPage />} />
              <Route path="/tenders" element={<TendersPage />} />
              <Route path="/tenders/:id" element={<TenderPage />} />
              <Route path="/customers" element={<CustomersPage />} />
              <Route path="/customers/:id" element={<CustomerPage />} />
              <Route path="/products" element={<ProductsPage />} />
              <Route path="/insights" element={<InsightsPage />} />
              <Route path="/settings" element={<SettingsPage />} />
              <Route path="*" element={<Navigate to="/overview" replace />} />
            </Route>
          </Routes>
        </BrowserRouter>
        <Toaster position="bottom-right" closeButton toastOptions={{ classNames: { toast: "group toast rounded-xl border border-black/[0.08] bg-white text-foreground shadow-float", title: "text-[13.5px] font-semibold", description: "text-[12.5px] text-muted-foreground", success: "[&_[data-icon]]:text-[hsl(var(--status-ok))]", error: "[&_[data-icon]]:text-[hsl(var(--status-bad))]", warning: "[&_[data-icon]]:text-[hsl(var(--status-warn))]", info: "[&_[data-icon]]:text-primary", closeButton: "bg-white border-black/[0.08] text-muted-foreground hover:text-foreground" } }} />
      </TooltipProvider>
    </AuthProvider>
  );
}

export default App;
